import type { ParsedJsonUpload, PipelineStepId } from './types';
import { formatJsonValue } from '~/app/utils/json';
import {
  buildAdieRoutingSummary,
  buildIcsRoutingSummary,
  extractAnomalyEventsFromAdieOutput,
  compactRecordObjects,
  filterLogsForAdie,
  filterLogsForIcs,
  unwrapLinLogs,
} from '@evimo/types/api/pipeline-debug/pipeline-routing';
import { buildPipelineMockRelations } from '@evimo/types/api/pipeline-debug/pipeline-ics-relations';
import { validatePipelineStepInput } from '@evimo/types/api/pipeline-debug/pipeline-step-input.validation';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readStringField(
  source: Record<string, unknown>,
  keys: string[],
): string {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim() !== '') {
      return value.trim();
    }
  }

  return '';
}

export function extractDeInputFromIcsOutput(
  icsOutput: unknown,
): unknown | null {
  const record = asRecord(icsOutput);
  if (!record || record.skipped === true) {
    return null;
  }

  const results = Array.isArray(record.results) ? record.results : [];

  for (const result of results) {
    const resultRecord = asRecord(result);
    if (!resultRecord || resultRecord.success === false) {
      continue;
    }

    const incident = asRecord(resultRecord.incident);
    if (!incident) {
      continue;
    }

    const incidentId =
      readStringField(incident, ['incident_id', 'incidentId']) ||
      readStringField(resultRecord, ['incident_id', 'incidentId']);
    const events = compactRecordObjects(
      Array.isArray(incident.events)
        ? incident.events
        : Array.isArray(incident.Events)
          ? incident.Events
          : [],
    );

    if (!incidentId || events.length === 0) {
      continue;
    }

    return {
      ...incident,
      incident_id: incidentId,
      events,
    };
  }

  return null;
}

function flattenUploadRecords(uploads: ParsedJsonUpload[]): unknown[] {
  return uploads.flatMap((upload) => upload.records);
}

export function parseJsonFileContent(
  fileName: string,
  content: string,
): { records: unknown[] } | { error: string } {
  try {
    const parsed = JSON.parse(content) as unknown;

    if (Array.isArray(parsed)) {
      return { records: parsed };
    }

    if (parsed !== null && typeof parsed === 'object') {
      const record = parsed as Record<string, unknown>;
      if (Array.isArray(record.rows)) {
        return { records: record.rows };
      }

      return { records: [parsed] };
    }

    return { error: `${fileName}: JSON root must be an object or array` };
  } catch {
    return { error: `${fileName}: invalid JSON` };
  }
}

export function validateStepInput(
  stepId: PipelineStepId,
  input: unknown,
): { success: true } | { success: false; error: string } {
  return validatePipelineStepInput(stepId, input);
}

export function buildInitialLinInput(uploads: ParsedJsonUpload[]): unknown[] {
  return flattenUploadRecords(uploads);
}

export function buildStepInput(
  stepId: PipelineStepId,
  previousOutput: unknown,
  uploads: ParsedJsonUpload[],
  linOutput?: unknown,
): unknown {
  if (stepId === 'lin') {
    return buildInitialLinInput(uploads);
  }

  if (stepId === 'adie') {
    const linLogs = unwrapLinLogs(previousOutput);
    const adieLogs = filterLogsForAdie(linLogs);

    return {
      logs: adieLogs,
      routing: buildAdieRoutingSummary(linLogs, adieLogs, []),
    };
  }

  if (stepId === 'ics') {
    const linLogs = unwrapLinLogs(linOutput ?? previousOutput);
    const icsLogs = filterLogsForIcs(linLogs);
    const anomalyEvents = compactRecordObjects(
      extractAnomalyEventsFromAdieOutput(previousOutput),
    );

    return {
      logs: icsLogs,
      anomaly_events: anomalyEvents,
      relations: buildPipelineMockRelations(icsLogs, anomalyEvents),
      routing: buildIcsRoutingSummary(linLogs, icsLogs, anomalyEvents),
    };
  }

  return extractDeInputFromIcsOutput(previousOutput) ?? previousOutput;
}

export function downloadJsonFile(fileName: string, payload: unknown): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function parseJsonEditorText(
  text: string,
): { success: true; value: unknown } | { success: false; error: string } {
  const trimmed = text.trim();

  if (!trimmed) {
    return { success: true, value: null };
  }

  try {
    return { success: true, value: JSON.parse(trimmed) as unknown };
  } catch {
    return { success: false, error: 'Invalid JSON' };
  }
}

export async function copyJsonToClipboard(value: unknown): Promise<boolean> {
  if (value === null || value === undefined) {
    return false;
  }

  await navigator.clipboard.writeText(formatJsonValue(value));
  return true;
}

export function summarizeStepOutput(
  stepId: PipelineStepId,
  output: unknown,
): string {
  const record = asRecord(output);

  if (stepId === 'lin' && Array.isArray(output)) {
    return `${output.length} normalized log batch(es)`;
  }

  if (stepId === 'adie') {
    const responses = Array.isArray(record?.responses)
      ? record.responses
      : Array.isArray(output)
        ? output
        : [];
    const anomalyCount = extractAnomalyEventsFromAdieOutput(output).length;
    const routing = asRecord(record?.routing);
    const forwarded = routing?.forwardedToAdie;
    const skipped = routing?.skippedNoTelemetries;

    if (record?.skipped === true) {
      return 'ADIE skipped — no telemetry logs';
    }

    if (typeof forwarded === 'number' && typeof skipped === 'number') {
      return `${anomalyCount} anomaly event(s) from ${forwarded} telemetry log(s); ${skipped} skipped (no telemetries)`;
    }

    return `${anomalyCount} anomaly event(s) from ${responses.length} response(s)`;
  }

  if (stepId === 'ics') {
    if (record?.skipped === true) {
      return 'ICS skipped — all logs are INFO or UNKNOWN';
    }

    const results = Array.isArray(record?.results) ? record.results : [];
    const routing = asRecord(record?.routing);
    const forwarded = routing?.forwardedToIcs;
    const anomalies = routing?.anomalyEvents;

    if (typeof forwarded === 'number' && typeof anomalies === 'number') {
      return `${results.length} correlation result(s) from ${forwarded} log(s) + ${anomalies} anomalie(s)`;
    }

    return `${results.length} incident correlation result(s)`;
  }

  const diagnosis = asRecord(output) ?? {};
  const rootCause = asRecord(diagnosis.rootCauseAnalysis)?.rootCause;
  return typeof rootCause === 'string'
    ? `Root cause: ${rootCause}`
    : 'Diagnosis generated';
}
